import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface RecurringBreak {
  id: string;
  day_of_week: number; // 0 = dimanche, 6 = samedi
  start_time: string;
  end_time: string;
  title: string | null;
  notes: string | null;
  is_active: boolean;
  valid_from: string | null;
  valid_until: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateRecurringBreakData {
  day_of_week: number;
  start_time: string;
  end_time: string;
  title?: string;
  notes?: string;
  is_active?: boolean;
  valid_from?: string | null;
  valid_until?: string | null;
}

/**
 * Étend les pauses récurrentes en pauses concrètes pour une date donnée.
 * Renvoie un format compatible avec generateTimeSlots ({ start_time, end_time, date }).
 */
export const expandRecurringBreaksForDate = (
  recurringBreaks: RecurringBreak[],
  dateStr: string,
  dayOfWeek: number,
) => {
  return recurringBreaks
    .filter((rb) => rb.is_active && rb.day_of_week === dayOfWeek)
    .filter((rb) => {
      if (rb.valid_from && dateStr < rb.valid_from) return false;
      if (rb.valid_until && dateStr > rb.valid_until) return false;
      return true;
    })
    .map((rb) => ({
      start_time: rb.start_time,
      end_time: rb.end_time,
      date: dateStr,
    }));
};

export const useRecurringBreaks = () => {
  const queryClient = useQueryClient();

  const { data: recurringBreaks = [], isLoading, refetch } = useQuery({
    queryKey: ["recurring_breaks"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("recurring_breaks")
        .select("*")
        .order("day_of_week", { ascending: true })
        .order("start_time", { ascending: true });

      if (error) throw error;
      return data as RecurringBreak[];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const createRecurringBreak = useMutation({
    mutationFn: async (newBreak: CreateRecurringBreakData) => {
      const { data, error } = await supabase
        .from("recurring_breaks")
        .insert([
          {
            day_of_week: newBreak.day_of_week,
            start_time: newBreak.start_time,
            end_time: newBreak.end_time,
            title: newBreak.title || "Pause",
            notes: newBreak.notes || null,
            is_active: newBreak.is_active ?? true,
            valid_from: newBreak.valid_from || null,
            valid_until: newBreak.valid_until || null,
          },
        ])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recurring_breaks"] });
      toast.success("Pause récurrente ajoutée");
    },
    onError: (error) => {
      console.error("Erreur création pause récurrente:", error);
      toast.error("Erreur lors de la création");
    },
  });

  const updateRecurringBreak = useMutation({
    mutationFn: async ({ id, ...data }: Partial<RecurringBreak> & { id: string }) => {
      const { data: updated, error } = await supabase
        .from("recurring_breaks")
        .update(data)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recurring_breaks"] });
      toast.success("Pause récurrente modifiée");
    },
    onError: (error) => {
      console.error("Erreur modification pause récurrente:", error);
      toast.error("Erreur lors de la modification");
    },
  });

  const deleteRecurringBreak = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("recurring_breaks")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recurring_breaks"] });
      toast.success("Pause récurrente supprimée");
    },
    onError: (error) => {
      console.error("Erreur suppression pause récurrente:", error);
      toast.error("Erreur lors de la suppression");
    },
  });

  return {
    recurringBreaks,
    isLoading,
    refetch,
    createRecurringBreak,
    updateRecurringBreak,
    deleteRecurringBreak,
  };
};
